import { useEffect, useRef, useState } from "react";
import type { MouseEvent, ReactNode } from "react";
import MuiButton from "@mui/material/Button";

import { buttonSx } from "./buttonStyles";

// Spinner time if the user comes back to this page
const LOADING_RESET_MS = 2000;

interface ButtonLinkHrefProps {
    to: string; // Full URL (store pages, external sites)
    className?: string;
    children: ReactNode;
    openInNewTab?: boolean;
}

// Same look as ButtonLink, for links that leave the site (a plain <a>, no router)
const ButtonLinkHref = ({ to, className, children, openInNewTab = false }: ButtonLinkHrefProps) => {
    const [loading, setLoading] = useState(false);
    const timerRef = useRef<ReturnType<typeof setTimeout>>();

    useEffect(() => () => clearTimeout(timerRef.current), []);

    const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
        if (loading) {
            event.preventDefault();
            return;
        }

        // A new tab leaves this page in place, so a spinner here would be misleading
        if (openInNewTab) return;

        setLoading(true);
        timerRef.current = setTimeout(() => setLoading(false), LOADING_RESET_MS);
    };

    return (
        <MuiButton
            component="a"
            href={to}
            target={openInNewTab ? "_blank" : undefined}
            rel={openInNewTab ? "noopener noreferrer" : undefined}
            onClick={handleClick}
            loading={loading}
            variant="contained"
            className={className}
            sx={buttonSx}
        >
            {children}
        </MuiButton>
    );
};

export default ButtonLinkHref;